import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, CheckCircle, XCircle, RefreshCw, MessageSquare, 
  FileText, Shield, BarChart3, Filter, ChevronLeft, ChevronRight, 
  Eye, Trash2, Hash, Users, Clock, AlertTriangle, TrendingUp,
  Search, MoreVertical, Info, X
} from 'lucide-react';
import { fetchWithAuth } from './lib/utils';
import { getUserId } from './Auth';
import Cookies from 'js-cookie';

const ModerationReviewDashboard = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [ContentType, setContentType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [filterChannel, setFilterChannel] = useState('');
  
  const [flaggedContent, setFlaggedContent] = useState({
    comments: { rows: [], count: 0 },
    feedback: { rows: [], count: 0 },
    posts: { rows: [], count: 0 },
    replies: { rows: [], count: 0 }
  });
  
  const [stats, setStats] = useState({
    comments: null,
    feedback: null,
    posts: null,
    replies: null
  });
  
  const [loading, setLoading] = useState({
    Content: false,
    action: false,
    stats: false
  });
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Base URL for API calls
  const API_BASE_URL = 'https://api.safe-space-app.com/api';

  // Fetch moderation statistics
  const fetchStats = useCallback(async () => {
    setLoading(prev => ({ ...prev, stats: true }));
    
    try {
      const [commentsStats, feedbackStats, postsStats, repliesStats] = await Promise.all([
        fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/comments/moderation/stats`).then(res => res.json()),
        fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/feedback/moderation/stats`).then(res => res.json()),
        fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/posts/moderation/stats`).then(res => res.json()),
        fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/replies/moderation/stats`).then(res => res.json())
      ]);
      
      setStats({
        comments: commentsStats,
        feedback: feedbackStats,
        posts: postsStats,
        replies: repliesStats
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  }, []);
  const toggleItemExpansion = (itemId) => {
    setExpandedItems((prevExpandedItems) => {
        if (prevExpandedItems.includes(itemId)) {
            return prevExpandedItems.filter((id) => id !== itemId); // Collapse the item
        } else {
            return [...prevExpandedItems, itemId]; // Expand the item
        }
    });
};
  // Fetch flagged Content
  const fetchFlaggedContent = useCallback(async () => {
    setLoading(prev => ({ ...prev, Content: true }));
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        status: 'flagged'
      });
      
      if (filterChannel) {
        params.append('channel_id', filterChannel);
      }

      // Fetch comments
      if (ContentType === 'all' || ContentType === 'comments') {
        const commentsResponse = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/comments/moderation/flagged?${params}`
        );
        if (commentsResponse.ok) {
          const commentsData = await commentsResponse.json();
          setFlaggedContent(prev => ({
            ...prev,
            comments: {
              rows: commentsData.comments || [],
              count: commentsData.pagination?.total || 0
            }
          }));
        }
      }
      
      // Fetch feedback
      if (ContentType === 'all' || ContentType === 'feedback') {
        const feedbackResponse = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/feedback/moderation/flagged?${params}`
        );
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          setFlaggedContent(prev => ({
            ...prev,
            feedback: {
              rows: feedbackData.feedback || [],
              count: feedbackData.pagination?.total || 0
            }
          }));
        }
      }
      
      // Fetch posts
      if (ContentType === 'all' || ContentType === 'posts') {
        const postsResponse = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/posts/moderation/flagged?${params}`
        );
        if (postsResponse.ok) {
          const postsData = await postsResponse.json();
          setFlaggedContent(prev => ({
            ...prev,
            posts: {
              rows: postsData.posts || [],
              count: postsData.pagination?.total || 0
            }
          }));
        }
      }
      
      // Fetch replies
      if (ContentType === 'all' || ContentType === 'replies') {
        const repliesResponse = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/replies/moderation/flagged?${params}`
        );
        if (repliesResponse.ok) {
          const repliesData = await repliesResponse.json();
          setFlaggedContent(prev => ({
            ...prev,
            replies: {
              rows: repliesData.replies || [],
              count: repliesData.pagination?.total || 0
            }
          }));
        }
      }
    } catch (err) {
      setError('Failed to fetch flagged Content');
      console.error('Error fetching flagged Content:', err);
    } finally {
      setLoading(prev => ({ ...prev, Content: false }));
      setSelectedItems([]);
    }
  }, [ContentType, currentPage, pageSize, filterChannel]);

  // Delete Content
  const deleteContent = async (itemId, type) => {
    const userId = Cookies.get('userId')
    console.log(`userID is ${userId}`);

    setLoading(prev => ({ ...prev, action: true }));
    setError(null);

    try {
      let endpoint;
      switch(type) {
        case 'comment':
          endpoint = `${process.env.REACT_APP_API_URL}/api/comments/${itemId}/admin?user_id=${userId}`;
          break;
        case 'feedback':
          endpoint = `${process.env.REACT_APP_API_URL}/api/feedback/${itemId}`;
          break;
        case 'post':
          endpoint = `${process.env.REACT_APP_API_URL}/api/posts/${itemId}`;
          break;
        case 'reply':
          endpoint = `${process.env.REACT_APP_API_URL}/api/replies/${itemId}`;
          break;
        default:
          throw new Error('Unknown Content type');
      }
      
      const response = await fetchWithAuth(endpoint, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete');
      }
      
      setSuccess(`${type} deleted successfully`);
      fetchFlaggedContent();
      fetchStats();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  // Perform bulk moderation action
  const performBulkAction = async (action, items, type) => {

        const userId = Cookies.get('userId')
    console.log(`userID is ${userId}`);
    if (items.length === 0) {
      setError('Please select items to moderate');
      return;
    }
    
    setLoading(prev => ({ ...prev, action: true }));
    setError(null);
    setSuccess(null);
    
    try {
      let endpoint, body;
      
      switch(type) {
        case 'comments':
          endpoint = `${process.env.REACT_APP_API_URL}/api/comments/moderation/bulk`;
          body = { comment_ids: items, action, moderator_id: userId };
          break;
        case 'feedback':
          endpoint = `${process.env.REACT_APP_API_URL}/api/feedback/moderation/bulk`;
          body = { feedback_ids: items, action, moderator_id: userId };
          break;
        case 'posts':
          endpoint = `${process.env.REACT_APP_API_URL}/api/posts/moderation/bulk`;
          body = { post_ids: items, action, moderator_id: userId };
          break;
        case 'replies':
          endpoint = `${process.env.REACT_APP_API_URL}/api/replies/moderation/bulk`;
          body = { reply_ids: items, action, moderator_id: userId };
          break;
        default:
          throw new Error('Unknown Content type');
      }
      
      const response = await fetchWithAuth(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform action');
      }
      
      const data = await response.json();
      setSuccess(data.message);
      
      fetchFlaggedContent();
      fetchStats();
      setSelectedItems([]);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  // Handle bulk action
  const handleBulkAction = async (action) => {
    const itemsByType = {
      comments: [],
      feedback: [],
      posts: [],
      replies: []
    };
    
    selectedItems.forEach(item => {
      itemsByType[item.type + 's'].push(item.id);
    });
    
    for (const [type, ids] of Object.entries(itemsByType)) {
      if (ids.length > 0) {
        await performBulkAction(action, ids, type);
      }
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId, type) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item.id === itemId && item.type === type);
      if (exists) {
        return prev.filter(item => !(item.id === itemId && item.type === type));
      } else {
        return [...prev, { id: itemId, type }];
      }
    });
  };

  // Check if item is selected
  const isItemSelected = (itemId, type) => {
    return selectedItems.some(item => item.id === itemId && item.type === type);
  };

  // Format score for display
  const formatScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    return (score * 100).toFixed(1) + '%';
  };

  // Get severity color and badge
  const getSeverityInfo = (score) => {
    if (score === null || score === undefined) return { color: 'gray', label: 'Unknown' };
    if (score > 0.7) return { color: 'red', label: 'High Risk' };
    if (score > 0.5) return { color: 'orange', label: 'Medium Risk' };
    if (score > 0.3) return { color: 'yellow', label: 'Low Risk' };
    return { color: 'green', label: 'Safe' };
  };

  useEffect(() => {
    fetchFlaggedContent();
    fetchStats();
  }, [fetchFlaggedContent, fetchStats]);

  // Calculate totals
  const totalItems = Object.values(flaggedContent).reduce((sum, Content) => sum + Content.count, 0);
  const totalPages = Math.ceil(totalItems / pageSize);

  // Render Content item
  const renderContentItem = (item, type) => {
    const severity = getSeverityInfo(item.moderation?.toxicity_score || item.toxicity_score);
    const isExpanded = expandedItems.has(item.id);
    
    return (
      <div key={`${type}-${item.id}`} className="group hover:bg-gray-50 transition-colors">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={isItemSelected(item.id, type)}
              onChange={() => toggleItemSelection(item.id, type)}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {type === 'comment' && <MessageSquare className="h-4 w-4 text-blue-500" />}
                  {type === 'feedback' && <FileText className="h-4 w-4 text-green-500" />}
                  {type === 'post' && <Hash className="h-4 w-4 text-purple-500" />}
                  {type === 'reply' && <MessageSquare className="h-4 w-4 text-indigo-500" />}
                  
                  <span className="text-sm font-medium text-gray-900 capitalize">{type}</span>
                  
                  <span className="text-sm text-gray-500">
                    by {item.author || item.user?.username || 'Anonymous'}
                  </span>
                  
                  <span className={`text-xs px-2 py-0.5 rounded-full bg-${severity.color}-100 text-${severity.color}-700`}>
                    {severity.label}
                  </span>
                  
                  {item.context?.is_reply && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Reply</span>
                  )}
                  {item.is_incident && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Incident</span>
                  )}
                  {item.context?.is_pinned && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Pinned</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {new Date(item.created_at || item.createdAt).toLocaleString()}
                  </span>
                  
                  <button
                    onClick={() => setShowDetailModal({ item, type })}
                    className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Eye className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="mb-3">
                {type === 'post' && item.title && (
                  <h4 className="font-medium text-gray-900 mb-1">{item.title}</h4>
                )}
                
                <p className={`text-gray-700 ${!isExpanded ? 'line-clamp-3' : ''}`}>
                  {item.text || item.Content || item.description}
                </p>
                
                {(item.text || item.Content || item.description)?.length > 200 && (
                  <button
                    onClick={() => toggleItemExpansion(item.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
              
              {/* Context */}
              <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-600">
                {item.context?.place_name && (
                  <span>
                    Place: <span className="font-medium text-gray-900">{item.context.place_name}</span>
                  </span>
                )}
                {item.place?.organization_name && (
                  <span>
                    Place: <span className="font-medium text-gray-900">{item.place.organization_name}</span>
                  </span>
                )}
                {item.context?.channel_name && (
                  <span>
                    Channel: <span className="font-medium text-gray-900">#{item.context.channel_name}</span>
                  </span>
                )}
                {item.context?.post_title && (
                  <span>
                    Post: <span className="font-medium text-gray-900">{item.context.post_title}</span>
                  </span>
                )}
                {item.feedback_type && (
                  <span>
                    Type: <span className="font-medium text-gray-900">{item.feedback_type}</span>
                  </span>
                )}
              </div>
              
              {/* Moderation scores */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                {['toxicity', 'severe_toxicity', 'identity_attack', 'insult', 'profanity', 'threat'].map(metric => {
                  const score = item[`${metric}_score`] || item.moderation?.[`${metric}_score`];
                  const metricSeverity = getSeverityInfo(score);
                  return (
                    <div key={metric} className="text-center">
                      <p className="text-xs text-gray-500 capitalize mb-1">
                        {metric.replace('_', ' ')}
                      </p>
                      <p className={`text-sm font-medium text-${metricSeverity.color}-600`}>
                        {formatScore(score)}
                      </p>
                    </div>
                  );
                })}
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {(item.flagged_reason || item.moderation?.flagged_reason) && (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      {item.flagged_reason || item.moderation?.flagged_reason}
                    </span>
                  )}
                </div>
                
              
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const contentTypeMap = {
                        'comment': 'comments',
                        'feedback': 'feedback', // feedback stays singular
                        'post': 'posts',
                        'reply': 'replies'
                      };
                      performBulkAction('approve', [item.id], contentTypeMap[type]);
                    }}
                    disabled={loading.action}
                    className="px-3 py-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    Approve
                  </button>
                  {/* <button
                    onClick={() => {
                      const contentTypeMap = {
                        'comment': 'comments',
                        'feedback': 'feedback',
                        'post': 'posts',
                        'reply': 'replies'
                      };
                      performBulkAction('remove', [item.id], contentTypeMap[type]);
                    }}
                    disabled={loading.action}
                    className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    Remove
                  </button> */}
                  <button
                    onClick={() => deleteContent(item.id, type)}
                    disabled={loading.action}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50 text-sm font-medium flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Overview tab Content
  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {Object.entries(stats).map(([type, stat]) => {
        const Icon = {
          comments: MessageSquare,
          feedback: FileText,
          posts: Hash,
          replies: Users
        }[type];
        
        const color = {
          comments: 'blue',
          feedback: 'green',
          posts: 'purple',
          replies: 'indigo'
        }[type];
        
        return (
          <div key={type} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 bg-${color}-100 rounded-lg`}>
                <Icon className={`h-6 w-6 text-${color}-600`} />
              </div>
              <TrendingUp className="h-5 w-5 text-gray-400" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 capitalize mb-2">{type}</h3>
            
            {loading.stats ? (
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
              </div>
            ) : stat ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Flagged</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {stat.overall?.flaggedCount || stat.flaggedCount || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Auto-moderated</span>
                  <span className="text-sm font-medium text-gray-900">
                    {stat.overall?.autoModeratedCount || stat.autoModeratedCount || 0}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Avg Toxicity</span>
                    <span className={`text-sm font-medium text-${getSeverityInfo(stat.scores?.avg_toxicity || stat.averageScores?.avg_toxicity).color}-600`}>
                      {formatScore(stat.scores?.avg_toxicity || stat.averageScores?.avg_toxicity)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No data available</p>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Moderation Dashboard</h1>
                  <p className="text-gray-600 mt-1">Review and moderate flagged Content across all platforms</p>
                </div>
              </div>
              
              <button
                onClick={() => {
                  fetchFlaggedContent();
                  fetchStats();
                }}
                disabled={loading.Content || loading.stats}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading.Content || loading.stats ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1">
            {['Overview', 'Content'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium capitalize transition-colors relative ${
                  activeTab === tab
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center animate-slideIn">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto p-1 hover:bg-red-100 rounded"
            >
              <X className="h-4 w-4 text-red-500" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center animate-slideIn">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-green-800">{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto p-1 hover:bg-green-100 rounded"
            >
              <X className="h-4 w-4 text-green-500" />
            </button>
          </div>
        )}

        {activeTab === 'Overview' && renderOverview()}

        {activeTab === 'Content' && (
          <>
            {/* Filters and Controls */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 flex flex-col sm:flex-row gap-4">
                  

                  <div className="relative flex-1 max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search Content..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  
                  <select
                    value={ContentType}
                    onChange={(e) => {
                      setContentType(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Content</option>
                    <option value="comments">Comments</option>
                    <option value="feedback">Feedback</option>
                    <option value="posts">Channel Posts</option>
                    <option value="replies">Channel Replies</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Filter by channel..."
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {selectedItems.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 mr-2">{selectedItems.length} selected</span>
                    <button
                      onClick={() => handleBulkAction('approve')}
                      disabled={loading.action}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium"
                    >
                      Approve All
                    </button>
                    <button
                      onClick={() => handleBulkAction('remove')}
                      disabled={loading.action}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                    >
                      Remove All
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {loading.Content ? (
                <div className="flex items-center justify-center py-24">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-200">
                    {/* Render all Content types */}
                    {(ContentType === 'all' || ContentType === 'comments') && 
                      flaggedContent.comments.rows.map(item => renderContentItem(item, 'comment'))}
                    
                    {(ContentType === 'all' || ContentType === 'feedback') && 
                      flaggedContent.feedback.rows.map(item => renderContentItem(item, 'feedback'))}
                    
                    {(ContentType === 'all' || ContentType === 'posts') && 
                      flaggedContent.posts.rows.map(item => renderContentItem(item, 'post'))}
                    
                    {(ContentType === 'all' || ContentType === 'replies') && 
                      flaggedContent.replies.rows.map(item => renderContentItem(item, 'reply'))}
                    
                    {/* Empty state */}
                    {totalItems === 0 && (
                      <div className="text-center py-24">
                        <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-xl text-gray-500 mb-2">No flagged Content to review</p>
                        <p className="text-gray-400">All Content is currently within acceptable limits</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1 || loading.Content}
                          className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        
                        <div className="flex gap-1">
                          {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            const page = i + 1;
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded-lg transition-colors ${
                                  currentPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'hover:bg-gray-200'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages || loading.Content}
                          className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages} • {totalItems} total items
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Content Details</h3>
              <button
                onClick={() => setShowDetailModal(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <pre className="whitespace-pre-wrap text-sm">
                {JSON.stringify(showDetailModal, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add these styles to your global CSS
const styles = `
  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .animate-slideIn {
    animation: slideIn 0.3s ease-out;
  }
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export default ModerationReviewDashboard;